<script setup>
// A stable, shareable link to a specific saved snapshot of a Builder3 page —
// distinct from /site/:id (which always shows whatever's currently live) and
// from the older /site-preview/:id (a live in-editor preview fed by
// postMessage, for the JSON-block builder, not raw HTML at all). Resolved by
// an unguessable token (see backend/routes/sites.js), not a sequential id,
// so a link can be handed out without exposing any other tenant's drafts.
// Builder3-only — there's no JSON-block/compiled-Vue-template path here.
import { computed, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { API_BASE_URL } from '@/stores/appConfig'
import { injectRawSiteShims } from '@/utils/rawSiteShims'
import SiteLoadingSpinner from '@/components/SiteLoadingSpinner.vue'

const route = useRoute()

const loading = ref(true)
const loadError = ref('')
const website = reactive(createEmptyWebsite())

// Increments for each load attempt so stale async responses cannot overwrite newer route data.
let loadRun = 0

const routeToken = computed(() => String(route.params.token || '').trim())

const rawSiteHtml = computed(() => injectRawSiteShims(website.content?.html))

const pageTitle = computed(() =>
  text(website.site_title || website.content?.title || 'Preview')
)

watch(
  pageTitle,
  (title) => {
    if (title) {
      document.title = `${title} (preview)`
    }
  },
  { immediate: true }
)

// Preview links are for sharing with a specific person, not for search
// engines to crawl — this must never get indexed.
document.head.querySelector('meta[name="robots"]')?.remove()
const robotsTag = document.createElement('meta')
robotsTag.setAttribute('name', 'robots')
robotsTag.setAttribute('content', 'noindex, nofollow')
document.head.appendChild(robotsTag)

function createEmptyWebsite() {
  return {
    id: null,
    template_id: '',
    site_title: '',
    meta_description: '',
    content: null,
  }
}

function resetWebsite() {
  Object.assign(website, createEmptyWebsite())
}

function parseContent(rawContent) {
  if (!rawContent) {
    return {}
  }

  if (typeof rawContent === 'string') {
    try {
      return JSON.parse(rawContent)
    }
    catch (error) {
      console.error('Unable to parse preview content:', error)
      return {}
    }
  }

  if (typeof rawContent === 'object') {
    return rawContent
  }

  return {}
}

function text(value) {
  if (value && typeof value === 'object' && 'value' in value) {
    return text(value.value)
  }

  return String(value || '').trim()
}

async function retrieveWebsite(token) {
  const currentRun = ++loadRun

  resetWebsite()
  loading.value = true
  loadError.value = ''

  if (!token) {
    loading.value = false
    loadError.value = 'No preview was specified.'
    return
  }

  try {
    const response = await fetch(`${API_BASE_URL}/sites/preview/${encodeURIComponent(token)}`)
    const data = await response.json().catch(() => ({}))

    if (currentRun !== loadRun) {
      return
    }

    if (!response.ok || !data.ok) {
      loadError.value = data.error || 'Preview not found.'
      return
    }

    const record = data.website || {}
    Object.assign(website, createEmptyWebsite(), {
      ...record,
      content: parseContent(record.content),
    })
  }
  catch (error) {
    if (currentRun !== loadRun) {
      return
    }

    console.error('retrieve preview error:', error)
    loadError.value = error.message || 'Failed to load preview.'
  }
  finally {
    if (currentRun === loadRun) {
      loading.value = false
    }
  }
}

watch(
  routeToken,
  (token) => {
    retrieveWebsite(token)
  },
  { immediate: true }
)
</script>

<template>
  <div class="site-view">
    <SiteLoadingSpinner v-if="loading" />

    <div
      v-else-if="loadError"
      class="site-status site-status-error"
    >
      {{ loadError }}
    </div>

    <iframe
      v-else-if="rawSiteHtml"
      class="raw-site-frame"
      sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
      :srcdoc="rawSiteHtml"
    />

    <div
      v-else
      class="site-status site-status-error"
    >
      Preview content not found.
    </div>
  </div>
</template>

<style scoped>
.site-view {
  min-height: 100vh;
}

.site-status {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 32px;
  color: var(--text-primary);
  background: var(--surface-alt);
  font: 600 1rem/1.4 system-ui, sans-serif;
}

.site-status-error {
  color: #9f2a1d;
}

.raw-site-frame {
  display: block;
  width: 100%;
  height: 100vh;
  border: none;
  background: #fff;
}
</style>
